import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Wallet, TrendingUp, Building2, ShieldCheck, FileSignature, Calendar, Plus, Download, Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FileUpload from '@/components/FileUpload';

const DOC_KINDS = [
  { id: 'cr_copy', label: 'CR Copy' },
  { id: 'baladiya_card', label: 'Baladiya Card' },
  { id: 'tax_card', label: 'Tax Card' },
  { id: 'moa', label: 'MOA' },
  { id: 'aoa', label: 'AOA' },
  { id: 'financial_statement', label: 'Financial Statement' },
  { id: 'audit_report', label: 'Audit Report' },
  { id: 'agreement', label: 'Agreement' },
  { id: 'board_resolution', label: 'Board Resolution' },
  { id: 'license', label: 'License' },
  { id: 'other', label: 'Other' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'financials', label: 'Financials', icon: TrendingUp },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'investments', label: 'Investments & Liabilities', icon: Wallet },
  { id: 'changes', label: 'Changes & Approvals', icon: ShieldCheck },
  { id: 'minutes', label: 'Board Minutes', icon: Calendar },
];

function field(label, value, mono = false) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`text-sm mt-1 ${mono ? 'mono' : ''}`}>{value ?? '—'}</div>
    </div>
  );
}

function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; }
function fmtMoney(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function EntityDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [tab, setTab] = useState('overview');
  const [financials, setFinancials] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [changes, setChanges] = useState([]);

  // financial form
  const [finForm, setFinForm] = useState({ period_year: new Date().getFullYear(), period_quarter: 1, revenue: 0, ebitda: 0, net_profit: 0, cash_position: 0, total_liabilities: 0 });
  const [finOpen, setFinOpen] = useState(false);

  // change request form
  const [chgForm, setChgForm] = useState({ kind: 'cr_update', title: '', summary: '' });
  const [chgOpen, setChgOpen] = useState(false);

  // Inline-edit state for legal_name + trade_name on the heading
  const [editing, setEditing] = useState(null); // 'legal_name' | 'trade_name' | null
  const [editDraft, setEditDraft] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  /**
   * Save a single-field edit on the entity. Optimistically updates the
   * local copy, falls back to a refetch on error. Holding admins +
   * system admins can edit; the backend `require_admin` guard already
   * enforces the same.
   */
  const saveField = async (field) => {
    const trimmed = editDraft.trim();
    if (field === 'legal_name' && trimmed.length < 1) {
      toast.error('Legal name cannot be empty.');
      return;
    }
    if (trimmed === (e?.[field] || '')) {
      setEditing(null);
      return;
    }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/v2/entities/${id}`, { [field]: trimmed || null });
      setE(data);
      toast.success(`${field === 'legal_name' ? 'Legal name' : 'Trade name'} updated.`);
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save.');
    } finally {
      setEditSaving(false);
    }
  };

  const beginEdit = (field) => {
    setEditing(field);
    setEditDraft(e?.[field] || '');
  };

  const load = async () => {
    try {
      const [ent, fin, mins, chg] = await Promise.all([
        api.get(`/v2/entities/${id}`),
        api.get(`/v2/entities/financials/${id}`).catch(() => ({ data: [] })),
        api.get('/minutes/', { params: { entity_id: id } }).catch(() => ({ data: [] })),
        api.get('/workflows/').catch(() => ({ data: [] })),
      ]);
      setE(ent.data);
      setFinancials(fin.data);
      setMinutes(mins.data);
      setChanges(chg.data.filter((r) => r.entity_id === id));
    } catch { toast.error('Could not load entity'); }
  };
  useEffect(() => { load(); }, [id]);

  const addFinancial = async (ev) => {
    ev.preventDefault();
    try {
      const payload = {
        entity_id: id,
        period_label: `Q${finForm.period_quarter} ${finForm.period_year}`,
        period_year: parseInt(finForm.period_year),
        period_quarter: parseInt(finForm.period_quarter),
        revenue: parseFloat(finForm.revenue) || 0,
        ebitda: parseFloat(finForm.ebitda) || 0,
        net_profit: parseFloat(finForm.net_profit) || 0,
        cash_position: parseFloat(finForm.cash_position) || 0,
        total_liabilities: parseFloat(finForm.total_liabilities) || 0,
      };
      await api.post('/v2/entities/financials', payload);
      toast.success('Financial period added.');
      setFinOpen(false);
      load();
    } catch { toast.error('Failed'); }
  };

  const submitChange = async (ev) => {
    ev.preventDefault();
    try {
      await api.post('/workflows/', { ...chgForm, entity_id: id });
      toast.success('Change request submitted — pending approvals.');
      setChgOpen(false);
      setChgForm({ kind: 'cr_update', title: '', summary: '' });
      load();
    } catch { toast.error('Failed'); }
  };

  const exportPdf = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/v2/entities/${id}/pdf`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(e?.legal_name || 'entity').replace(/[^a-z0-9]/gi, '_')}_brief.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded.');
    } catch { toast.error('Could not export PDF.'); }
  };

  if (!e) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Link to="/dashboard/entities-os" className="ve-btn-ghost text-xs"><ArrowLeft size={12}/> All entities</Link>
        <button onClick={exportPdf} data-testid="entity-export-pdf" className="ve-btn-secondary text-xs">
          <Download size={12}/> Export PDF
        </button>
      </div>
      <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="ve-eyebrow">{e.jurisdiction} · {e.legal_type}</div>

          {/* Legal name — inline editable for any admin */}
          {editing === 'legal_name' ? (
            <div className="mt-2 flex items-center gap-2" data-testid="entity-legal-name-edit">
              <input
                value={editDraft}
                onChange={(ev) => setEditDraft(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') saveField('legal_name');
                  if (ev.key === 'Escape') setEditing(null);
                }}
                autoFocus
                disabled={editSaving}
                data-testid="entity-legal-name-input"
                className="display text-3xl md:text-4xl font-bold bg-transparent border-b-2 border-[#8B3FC5] focus:outline-none px-1 py-0.5 min-w-0 flex-1"
                maxLength={200}
              />
              <button
                onClick={() => saveField('legal_name')}
                disabled={editSaving}
                aria-label="Save legal name"
                data-testid="entity-legal-name-save"
                className="h-9 w-9 rounded-md bg-[#8B3FC5] text-white hover:bg-[#5E1F8B] disabled:opacity-50 flex items-center justify-center transition"
              >
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              <button
                onClick={() => setEditing(null)}
                disabled={editSaving}
                aria-label="Cancel edit"
                data-testid="entity-legal-name-cancel"
                className="h-9 w-9 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="group flex items-center gap-2 mt-2">
              <h1 className="display text-3xl md:text-4xl font-bold truncate" data-testid="entity-legal-name">
                {e.legal_name}
              </h1>
              <button
                onClick={() => beginEdit('legal_name')}
                aria-label="Edit legal name"
                data-testid="entity-legal-name-edit-btn"
                title="Edit legal name"
                className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md text-slate-400 hover:text-[#5E1F8B] hover:bg-[#FFF8E5] flex items-center justify-center transition"
              >
                <Pencil size={13} />
              </button>
            </div>
          )}

          {/* Trade name (optional) — inline editable, with placeholder when empty */}
          {editing === 'trade_name' ? (
            <div className="mt-1 flex items-center gap-2" data-testid="entity-trade-name-edit">
              <span className="text-sm text-slate-500 shrink-0">trading as</span>
              <input
                value={editDraft}
                onChange={(ev) => setEditDraft(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') saveField('trade_name');
                  if (ev.key === 'Escape') setEditing(null);
                }}
                autoFocus
                disabled={editSaving}
                placeholder="(leave blank to clear)"
                data-testid="entity-trade-name-input"
                className="text-sm bg-transparent border-b border-[#8B3FC5] focus:outline-none px-1 py-0.5 min-w-0 flex-1"
                maxLength={200}
              />
              <button
                onClick={() => saveField('trade_name')}
                disabled={editSaving}
                aria-label="Save trade name"
                data-testid="entity-trade-name-save"
                className="h-7 w-7 rounded bg-[#8B3FC5] text-white hover:bg-[#5E1F8B] disabled:opacity-50 flex items-center justify-center transition"
              >
                {editSaving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              </button>
              <button
                onClick={() => setEditing(null)}
                disabled={editSaving}
                aria-label="Cancel edit"
                data-testid="entity-trade-name-cancel"
                className="h-7 w-7 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition"
              >
                <X size={11} />
              </button>
            </div>
          ) : (
            <div className="group flex items-center gap-2 mt-1">
              <p className="text-sm text-slate-500" data-testid="entity-trade-name">
                {e.trade_name ? <>trading as &ldquo;{e.trade_name}&rdquo;</> : <span className="italic">no trade name</span>}
              </p>
              <button
                onClick={() => beginEdit('trade_name')}
                aria-label="Edit trade name"
                data-testid="entity-trade-name-edit-btn"
                title={e.trade_name ? 'Edit trade name' : 'Add trade name'}
                className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded-md text-slate-400 hover:text-[#5E1F8B] hover:bg-[#FFF8E5] flex items-center justify-center transition"
              >
                <Pencil size={11} />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="ve-card px-4 py-2 text-xs">
            <div className="text-slate-500 uppercase tracking-[0.18em] font-bold">Equity</div>
            <div className="display text-lg text-[#5E1F8B] font-bold">{e.our_equity_percentage?.toFixed(1)}%</div>
          </div>
          <div className="ve-card px-4 py-2 text-xs">
            <div className="text-slate-500 uppercase tracking-[0.18em] font-bold">Invested</div>
            <div className="display text-lg text-[#5E1F8B] font-bold">{fmtMoney(e.total_invested)}</div>
          </div>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`entity-tab-${t.id}`}
            className={`px-4 py-2 text-sm rounded-full border transition-all ${tab===t.id ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#5E1F8B] hover:text-[#5E1F8B]'}`}>
            <t.icon size={14} className="inline -mt-0.5 mr-1.5"/>{t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 ve-card p-6 space-y-6">
              <h2 className="display text-xl font-bold">Legal & Registration</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {field('Legal Type', e.legal_type)}
                {field('Jurisdiction', e.jurisdiction)}
                {field('Status', <span className="capitalize">{e.status}</span>)}
                {field('CR Number', e.cr_number, true)}
                {field('CR Expiry', fmtDate(e.cr_expiry_date), true)}
                {field('Baladiya Card', e.baladiya_card_number, true)}
                {field('Baladiya Expiry', fmtDate(e.baladiya_expiry_date), true)}
                {field('Tax Card', e.tax_card_number, true)}
                {field('Tax Expiry', fmtDate(e.tax_card_expiry), true)}
                {field('Incorporation', fmtDate(e.incorporation_date))}
                {field('Sector', e.sector)}
                {field('Address', e.registered_address)}
              </div>

              <div className="ve-silver-divider"/>

              <h2 className="display text-xl font-bold">Relationship</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {field('Type', <span className="capitalize">{(e.relationship_type || '').replace(/_/g, ' ')}</span>)}
                {field('Our Equity', `${e.our_equity_percentage?.toFixed(1) ?? 0}%`, true)}
                {field('Contact', e.primary_contact_name)}
                {field('Contact Role', e.primary_contact_role)}
                {field('Email', e.primary_contact_email)}
                {field('Phone', e.primary_contact_phone, true)}
              </div>

              {e.shareholders?.length > 0 && (
                <>
                  <div className="ve-silver-divider"/>
                  <h2 className="display text-xl font-bold">Shareholders</h2>
                  <div className="space-y-2">
                    {e.shareholders.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-md bg-slate-50">
                        <div>
                          <div className="text-sm font-medium">{s.name}</div>
                          <div className="text-xs text-slate-500 capitalize">{s.type}</div>
                        </div>
                        <div className="display text-lg text-[#5E1F8B] font-bold">{s.percentage}%</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="ve-card p-6">
              <FileUpload entityId={id} kind="cr_copy" allowedKinds={DOC_KINDS} label="Documents"/>
            </div>
          </div>
        )}

        {tab === 'financials' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-xl font-bold">Financial periods</h2>
              <button onClick={() => setFinOpen(true)} data-testid="fin-add" className="ve-btn-primary text-xs px-4 py-2"><Plus size={12}/> Add period</button>
            </div>
            <div className="ve-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-[10px] font-bold">
                  <tr>
                    <th className="text-left px-5 py-3">Period</th>
                    <th className="text-right px-5 py-3">Revenue</th>
                    <th className="text-right px-5 py-3">EBITDA</th>
                    <th className="text-right px-5 py-3">Net Profit</th>
                    <th className="text-right px-5 py-3">Cash</th>
                    <th className="text-right px-5 py-3">Liabilities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {financials.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-500">No financial data submitted yet.</td></tr>
                  )}
                  {financials.map((f) => (
                    <tr key={f.period_id}>
                      <td className="px-5 py-3 font-medium">{f.period_label}</td>
                      <td className="px-5 py-3 text-right mono">{fmtMoney(f.revenue)}</td>
                      <td className="px-5 py-3 text-right mono">{fmtMoney(f.ebitda)}</td>
                      <td className="px-5 py-3 text-right mono">{fmtMoney(f.net_profit)}</td>
                      <td className="px-5 py-3 text-right mono">{fmtMoney(f.cash_position)}</td>
                      <td className="px-5 py-3 text-right mono">{fmtMoney(f.total_liabilities)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <FileUpload entityId={id} kind="financial_statement" allowedKinds={DOC_KINDS} label="Financial documents"/>
            </div>
          </div>
        )}

        {tab === 'documents' && (
          <div className="ve-card p-6">
            <FileUpload entityId={id} kind="other" allowedKinds={DOC_KINDS} label="All entity documents"/>
          </div>
        )}

        {tab === 'investments' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="ve-card p-6">
              <h3 className="display text-lg font-bold">Our exposure</h3>
              <div className="mt-5 space-y-4">
                {field('Total invested', `QAR ${fmtMoney(e.total_invested)}`, true)}
                {field('Outstanding liabilities', `QAR ${fmtMoney(e.outstanding_liabilities)}`, true)}
                {field('Dividends YTD', `QAR ${fmtMoney(e.dividends_received_ytd)}`, true)}
                {field('Net position', `QAR ${fmtMoney((e.total_invested || 0) - (e.outstanding_liabilities || 0))}`, true)}
              </div>
            </div>
            <div className="ve-card p-6">
              <FileUpload entityId={id} kind="agreement" allowedKinds={DOC_KINDS} label="Investment agreements & guarantees"/>
            </div>
          </div>
        )}

        {tab === 'changes' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-xl font-bold">Change requests & approvals</h2>
              <button onClick={() => setChgOpen(true)} data-testid="chg-add" className="ve-btn-primary text-xs px-4 py-2"><FileSignature size={12}/> Request change</button>
            </div>
            <div className="space-y-3">
              {changes.length === 0 && <div className="ve-card p-8 text-center text-sm text-slate-500">No change requests yet for this entity.</div>}
              {changes.map((c) => (
                <Link key={c.request_id} to={`/dashboard/approvals/${c.request_id}`} className="block ve-card p-5 hover:border-[#8B3FC5] transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">{c.kind.replace(/_/g, ' ')}</div>
                      <h3 className="font-bold mt-1">{c.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-1">{c.summary}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded font-bold ${
                      c.status === 'approved' || c.status === 'executed' ? 'bg-emerald-50 text-emerald-700' :
                      c.status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                      'bg-amber-50 text-amber-700'
                    }`}>{c.status.replace('_', ' ')}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    {c.workflow_steps?.map((s, i) => (
                      <div key={i} className={`flex-1 h-1.5 rounded-full ${i < (c.current_step || 0) || c.status === 'approved' || c.status === 'executed' ? 'bg-[#8B3FC5]' : c.status === 'rejected' && i === (c.current_step || 0) ? 'bg-rose-400' : 'bg-slate-200'}`}/>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {tab === 'minutes' && (
          <div>
            <h2 className="display text-xl font-bold mb-4">Board & management minutes</h2>
            <div className="space-y-3">
              {minutes.length === 0 && <div className="ve-card p-8 text-center text-sm text-slate-500">No minutes yet for this entity. <Link to="/dashboard/minutes" className="text-[#5E1F8B] font-bold">Create one →</Link></div>}
              {minutes.map((m) => (
                <Link key={m.minutes_id} to={`/dashboard/minutes/${m.minutes_id}`} className="block ve-card p-5 hover:border-[#8B3FC5] transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">{fmtDate(m.meeting_date)}</div>
                      <h3 className="font-bold mt-1">{m.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{m.items?.length || 0} agenda items · {m.attendees?.length || 0} attendees</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add financial period modal */}
      {finOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(ev) => ev.target === ev.currentTarget && setFinOpen(false)}>
          <form onSubmit={addFinancial} className="ve-card p-7 w-full max-w-xl bg-white">
            <h2 className="display text-2xl font-bold">Add financial period</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Year
                <input data-testid="fin-year" type="number" required value={finForm.period_year} onChange={(ev) => setFinForm({...finForm, period_year: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Quarter (0=annual)
                <input data-testid="fin-quarter" type="number" min={0} max={4} required value={finForm.period_quarter} onChange={(ev) => setFinForm({...finForm, period_quarter: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-normal"/>
              </label>
              {['revenue', 'ebitda', 'net_profit', 'cash_position', 'total_liabilities'].map((k) => (
                <label key={k} className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{k.replace(/_/g, ' ')} (QAR)
                  <input data-testid={`fin-${k}`} type="number" step="any" value={finForm[k]} onChange={(ev) => setFinForm({...finForm, [k]: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-normal"/>
                </label>
              ))}
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setFinOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid="fin-submit" className="ve-btn-primary">Add</button>
            </div>
          </form>
        </div>
      )}

      {/* Change request modal */}
      {chgOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(ev) => ev.target === ev.currentTarget && setChgOpen(false)}>
          <form onSubmit={submitChange} className="ve-card p-7 w-full max-w-xl bg-white">
            <h2 className="display text-2xl font-bold">Request a change</h2>
            <p className="text-sm text-slate-500 mt-1">Routes through Legal → CFO/CEO → Chairman by default.</p>
            <div className="mt-6 space-y-4">
              <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Kind
                <select data-testid="chg-kind" value={chgForm.kind} onChange={(ev) => setChgForm({...chgForm, kind: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal">
                  {['cr_update','baladiya_update','tax_card_update','shareholder_change','legal_doc_update','agreement_update','other'].map((k) =>
                    <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
                  )}
                </select>
              </label>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Title
                <input data-testid="chg-title" required value={chgForm.title} onChange={(ev) => setChgForm({...chgForm, title: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
              </label>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Summary
                <textarea data-testid="chg-summary" required rows={4} value={chgForm.summary} onChange={(ev) => setChgForm({...chgForm, summary: ev.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setChgOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid="chg-submit" className="ve-btn-primary">Submit for approval</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
